/**
 * Vuex Getters —— 派生数据层（Layer 4: Derived Data）
 *
 * 所有从主数据/业务记录计算得出的字段，统一在此定义。
 * 禁止在组件中重复计算相同的派生逻辑。
 */

import { calculateExpertPoints, calculateExpertContentCount } from '../data/experts'
import { SCORING_CONFIG, PERMISSIONS, isSuperAdmin } from '../data/adminConfig'

export default {
  /**
   * 带派生数据的完整专家列表（按积分降序）
   */
  expertsWithDerived: (state) => {
    const casesMap = state.cases || {}
    return state.experts.map(expert => {
      const points = calculateExpertPoints(expert.stats, SCORING_CONFIG.weights)
      const contentCount = calculateExpertContentCount(expert.id, casesMap)
      return {
        ...expert,
        points,
        contentCount
      }
    }).sort((a, b) => b.points - a.points)
  },

  /**
   * 专家排行榜（带排名序号）
   */
  expertLeaderboard: (state, getters) => {
    return getters.expertsWithDerived.map((expert, index) => ({
      ...expert,
      rank: index + 1
    }))
  },

  /**
   * 首页推荐专家
   */
  featuredExperts: (state, getters) => {
    return getters.expertsWithDerived.filter(
      e => e.isFeatured || e.recommendWeight > 0
    ).sort((a, b) => b.recommendWeight - a.recommendWeight)
  },

  /**
   * 活跃专家数
   */
  activeExpertCount: (state) => {
    return state.experts.filter(e => e.status === 'active').length
  },

  /**
   * 用户学习总时长（分钟）
   */
  totalStudyDuration: (state) => {
    return state.studyRecords.reduce((sum, r) => sum + (r.duration || 0), 0)
  },

  /**
   * 用户练习正确率
   */
  practiceAccuracy: (state) => {
    if (!state.practiceRecords.length) return 0
    const correct = state.practiceRecords.filter(r => r.isCorrect).length
    return Math.round((correct / state.practiceRecords.length) * 100)
  },

  /**
   * 当前诊断历史（按时间降序）
   */
  sortedHistory: (state) => {
    return [...state.history].sort((a, b) => b.timestamp - a.timestamp)
  },

  // ========== 权限系统 Getters ==========

  /**
   * 当前登录的管理员账户
   */
  currentAdmin: (state) => {
    return state.admins.find(a => a.id === state.currentAdminId) || null
  },

  /**
   * 当前管理员的权限列表
   * 超级管理员自动拥有所有权限
   */
  currentPermissions: (state, getters) => {
    const admin = getters.currentAdmin
    if (!admin) return []
    if (isSuperAdmin(admin.role)) return Object.values(PERMISSIONS)
    return admin.permissions || []
  },

  /**
   * 当前管理员的数据范围
   * all = 全部内容, own = 仅自己创建的内容
   */
  dataScope: (state, getters) => {
    const admin = getters.currentAdmin
    if (!admin) return 'own'
    if (isSuperAdmin(admin.role)) return 'all'
    return admin.dataScope || 'own'
  },

  /**
   * 当前是否是超级管理员
   */
  isSuperAdmin: (state, getters) => {
    return isSuperAdmin(getters.currentAdmin?.role)
  },

  /**
   * 权限检查函数
   * 用法：v-if="hasPermission(PERMISSIONS.EDIT_TOPIC)"
   */
  hasPermission: (state, getters) => (permission) => {
    return getters.currentPermissions.includes(permission)
  },

  /**
   * 当前管理员ID（用于数据创建者标记）
   */
  currentAdminId: (state) => {
    return state.currentAdminId
  },
}
